import { useContext, useMemo } from 'react';
import { ReactReduxContext } from 'react-redux';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import Dialog from '@comp/Dialog/Dialog';
import CloudDownload from '@icon/CloudDownload';
import Image from '@base/Image';
import { Spacings } from '@styles/index';
import { DPP_STATE } from '@educator/Container/AutoDPP/constants';
import { PRACTICE_FLOW_TYPE, PRACTICE_TYPE } from '@cont/Practice/constants';
import { PDF_DOWNLOAD } from '@constants/segment';
import useFinishSummary from '@stores/auto-dpp/dppFinishSummaryStore';
import usePracticeStore from '@stores/planner/practiceStore';
import { finishPracticeSession } from '@actions/practiceSession';
import { finishPracticeSession as finishPlannerPracticeApi } from '@api/fetchers/practice';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useTopology from '@api/hooks/topology/useTopology';
import segmentStorage from '@events/segmentStorage';
import pushRoute from '@utils/pushRoute';
import {
  classDidNotHappen,
  getEntityData as getItemEntityData
} from '@models/Item';
import { NEOLESSON } from '@utils/constants';
import useFetch from '@hooks/useFetch';
import {
  CloseIcon,
  ClickableSection,
  Container,
  Divider,
  MetaText,
  PdfButton,
  PdfButtons,
  Section,
  SectionBody,
  SectionIcon,
  SectionMeta,
  SectionTitle,
  Subject,
  TitleText
} from './DppMoreDialog.styles';
import {
  COMPLETED_STATUS,
  INCOMPLETE_STATUS,
  getDppMoreClickEvents,
  getDppMoreEventProps,
  getLiveAtLabel,
  getParentName,
  getSubjectColor,
  getSubjectName,
  getSubjectUid,
  normalizePlannerQuiz,
  normalizePracticeDpp,
  plannerClassFetcherOptions
} from './dppMoreDialogUtils';

const UNAVAILABLE_AFTER_CLASS = 'Will be available after class';
const UNAVAILABLE_POST_PURCHASE = 'Will be available post purchase';

const DppMoreDialog = ({
  showDialog,
  onClose,
  lesson,
  goalUID,
  source,
  isDisabled,
  isLockedForPurchase
}) => {
  const reduxContext = useContext(ReactReduxContext);
  const { query } = useRouter();
  const plannerClassKey =
    source === 'Planner' && showDialog && lesson?.uid
      ? `v1/uplus/classes/${lesson.uid}/info_v2/`
      : null;
  const { data: plannerClassInfo } = useFetch(
    plannerClassKey,
    plannerClassFetcherOptions
  );
  const lessonData = useMemo(() => {
    if (source !== 'Planner' || !plannerClassInfo) return lesson;
    const details = getItemEntityData(plannerClassInfo);
    return {
      ...lesson,
      slides: details.slides || lesson.slides,
      classType: details.classType || lesson.classType,
      practiceDpp: details.practiceDpp || lesson.practiceDpp,
      topicGroups: details.topicGroups?.length
        ? details.topicGroups
        : lesson.topicGroups
    };
  }, [lesson, plannerClassInfo, source]);
  const retrievedGoalUID = goalUID || lesson.goalUid;
  const unavailableMeta = isLockedForPurchase
    ? UNAVAILABLE_POST_PURCHASE
    : UNAVAILABLE_AFTER_CLASS;
  const { topology } = useTopology(retrievedGoalUID);
  const goalSegmentData = useGoalEventProps({ goalUID: retrievedGoalUID });
  const { toggleShowSummary, setFinishSummary } = useFinishSummary();
  const { finishPlannerPracticeSession } = usePracticeStore();

  const isNeoLesson = lessonData.classType === NEOLESSON;
  const slides = lessonData.slides || {};
  const showClassPdf = !classDidNotHappen(lesson) && !!lessonData.slides;
  const showWithAnnotation = showClassPdf && !!slides.withAnnotations;
  const showWithoutAnnotation =
    showClassPdf && !isNeoLesson && !!slides.withoutAnnotations;
  const showPdfSection =
    isDisabled || showWithAnnotation || showWithoutAnnotation;

  const lessonPracticeDpp = lessonData?.practiceDpp || {};
  const plannerQuiz =
    source === 'Planner'
      ? lesson?.attachments?.quizzes?.[0]
      : lessonData?.attachments?.quizzes?.[0];
  const practiceDppData =
    source === 'Planner'
      ? normalizePlannerQuiz(plannerQuiz)
      : normalizePracticeDpp(lessonPracticeDpp) ||
        normalizePlannerQuiz(plannerQuiz);
  const {
    status,
    sessionUid,
    solvedQuestions = 0,
    numQuestions = 0,
    uid: quizUID
  } = practiceDppData || {};
  const hasCourseEducatorPractice = plannerQuiz
    ? !!plannerQuiz.uid || !!quizUID
    : lessonPracticeDpp.state === DPP_STATE.PUBLISHED || !!quizUID;
  const hasEducatorPractice =
    source === 'Planner' ? Boolean(quizUID) : hasCourseEducatorPractice;
  const isCompleted = status === COMPLETED_STATUS;
  const isInComplete = status === INCOMPLETE_STATUS;
  const topicGroupUid =
    source === 'Planner'
      ? lesson?.topicGroup?.uid
      : practiceDppData?.topicGroupUid || getSubjectUid(lessonData);
  const goalSlug =
    practiceDppData?.goalSlug ||
    lesson?.goal?.slug ||
    query?.goalSlug ||
    topology?.goalSlug;
  const educatorName = lesson.author?.name || '';
  const subjectName = getSubjectName(lessonData);
  const subjectColor = getSubjectColor(lessonData);
  const liveAtLabel = getLiveAtLabel(lesson);
  const parentName = getParentName(lesson);
  const lessonTitle = lesson.name;
  const classDpp =
    source === 'Planner' ? lesson?.attachments?.dpp : lessonData?.dpp;
  const showDppRow = isDisabled || classDpp?.url;
  const practiceSource = source || 'Course';
  const { dpp: dppClickedEvent, practice: practiceClickedEvent } =
    getDppMoreClickEvents(practiceSource);
  const dppMoreEventProps = getDppMoreEventProps({
    lesson,
    lessonTitle,
    goalUID: retrievedGoalUID,
    goalName: goalSegmentData.goal_name,
    lastPrimarySourceSection: segmentStorage.getItem('lpss') || practiceSource
  });

  const getPracticeTitle = () => {
    if (isDisabled || !hasEducatorPractice) return 'Practice';
    if (isCompleted) return 'View practice';
    if (isInComplete && sessionUid && solvedQuestions >= 1) {
      return 'Resume practice';
    }
    return 'Start practice';
  };

  const getPracticeMeta = () => {
    if (isDisabled) return unavailableMeta;
    if (!hasEducatorPractice) return null;
    if (isCompleted) return 'Completed';
    if (isInComplete && sessionUid && solvedQuestions >= 1) {
      return `${
        numQuestions - solvedQuestions
      } of ${numQuestions} questions remaining`;
    }
    return numQuestions ? `${numQuestions} questions` : null;
  };

  const handlePracticeClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isDisabled) return;
    segment.track(practiceClickedEvent, {
      ...dppMoreEventProps,
      quiz_uid: hasEducatorPractice ? quizUID || '' : '',
      is_educator_curated: hasEducatorPractice
    });
    onClose();
    if (hasEducatorPractice && isCompleted && sessionUid) {
      if (source === 'Planner' || plannerQuiz) {
        const { session_report } = await finishPlannerPracticeApi(sessionUid);
        finishPlannerPracticeSession(session_report, true);
        setFinishSummary({
          goalSlug,
          goalUID: retrievedGoalUID,
          sessionId: sessionUid,
          type: PRACTICE_FLOW_TYPE.AUTO_DPP,
          topicGroup: topicGroupUid,
          topicUID: topicGroupUid,
          quizUID,
          educatorName
        });
        toggleShowSummary();
        return pushRoute(
          {
            pathname: '/goal/[goalSlug]/[goalUID]/practice/'
          },
          `/goal/${goalSlug}/${retrievedGoalUID}/practice/`
        );
      }
      finishPlannerPracticeSession({}, false);
      if (reduxContext?.store) {
        await reduxContext.store.dispatch(finishPracticeSession(sessionUid));
      } else {
        await finishPlannerPracticeApi(sessionUid);
      }
      setFinishSummary({
        goalSlug,
        goalUID: retrievedGoalUID,
        sessionId: sessionUid,
        topicGroup: topicGroupUid,
        topicUID: topicGroupUid,
        quizUID,
        type: PRACTICE_FLOW_TYPE.AUTO_DPP,
        educatorName
      });
      return toggleShowSummary();
    }
    if (
      hasEducatorPractice &&
      quizUID &&
      topicGroupUid &&
      retrievedGoalUID &&
      goalSlug
    ) {
      return pushRoute({
        pathname: '/goal/[goalSlug]/[goalUID]/practice/[topicUID]',
        query: {
          goalSlug,
          goalUID: retrievedGoalUID,
          topic_type: PRACTICE_TYPE.EACH_TOPIC,
          type: PRACTICE_FLOW_TYPE.AUTO_DPP,
          topic_group: topicGroupUid,
          topicUID: topicGroupUid,
          quiz_uid: quizUID,
          educator_name: educatorName,
          source: practiceSource
        }
      });
    }
    if (retrievedGoalUID && goalSlug) {
      return pushRoute({
        pathname: '/goal/[goalSlug]/[goalUID]/practice',
        query: {
          goalSlug,
          goalUID: retrievedGoalUID
        }
      });
    }
  };

  const trackPdf = (downloadType, isAnnotationOn, url) => {
    if (isDisabled || !url) return;
    segment.track(PDF_DOWNLOAD, {
      ...goalSegmentData,
      download_type: downloadType,
      is_annotation_on: isAnnotationOn,
      last_primary_source: segmentStorage.getItem('lps'),
      lesson_uid: lesson.uid,
      lesson_title: lesson.name,
      educator_uid: lesson.author?.uid,
      educator_username: lesson.author?.name
    });
    window.open(url, '_blank');
    onClose();
  };

  const handleDppClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isDisabled || !classDpp?.url) return;
    segment.track(dppClickedEvent, {
      ...dppMoreEventProps,
      dpp_uid: classDpp.uid || ''
    });
    window.open(classDpp.url, '_blank');
    onClose();
  };

  return (
    <Dialog
      center
      onClose={onClose}
      dialogState={showDialog}
      borderRadius={Spacings.SPACING_2B}
    >
      <CloseIcon size={Spacings.SPACING_4B} onClick={onClose} />
      <Container>
        {subjectName ? (
          <Subject $color={subjectColor}>{subjectName}</Subject>
        ) : null}
        <TitleText $hasSubject={!!subjectName}>{lessonTitle}</TitleText>
        {parentName && parentName !== lessonTitle ? (
          <MetaText>{parentName}</MetaText>
        ) : null}
        {liveAtLabel ? <MetaText>{liveAtLabel}</MetaText> : null}

        {showPdfSection && (
          <>
            <Divider />
            <Section style={isDisabled ? { opacity: 0.45 } : undefined}>
              <SectionIcon $color={subjectColor} $disabled={isDisabled}>
                <Image
                  src="batch-pdf.svg"
                  width={40}
                  height={40}
                  alt="Class PDF"
                />
              </SectionIcon>
              <SectionBody>
                <SectionTitle>Class PDF</SectionTitle>
                {isDisabled && <SectionMeta>{unavailableMeta}</SectionMeta>}
              </SectionBody>
            </Section>
            {!isDisabled && (
              <PdfButtons>
                {showWithAnnotation && (
                  <PdfButton
                    type="button"
                    className="slides"
                    onClick={() =>
                      trackPdf('With annotations', true, slides.withAnnotations)
                    }
                  >
                    <CloudDownload
                      size="16"
                      color="var(--color-text-secondary)"
                    />
                    With annotation
                  </PdfButton>
                )}
                {showWithoutAnnotation && (
                  <PdfButton
                    type="button"
                    className="slides"
                    onClick={() =>
                      trackPdf(
                        'Without annotations',
                        false,
                        slides.withoutAnnotations
                      )
                    }
                  >
                    <CloudDownload
                      size="16"
                      color="var(--color-text-secondary)"
                    />
                    Without annotation
                  </PdfButton>
                )}
              </PdfButtons>
            )}
          </>
        )}

        {showDppRow && (
          <>
            <Divider />
            <ClickableSection
              type="button"
              $disabled={isDisabled}
              onClick={handleDppClick}
            >
              <SectionIcon $color={subjectColor} $disabled={isDisabled}>
                <Image src="batch-dpp.svg" width={40} height={40} alt="DPP" />
              </SectionIcon>
              <SectionBody>
                <SectionTitle>DPP</SectionTitle>
                {isDisabled && <SectionMeta>{unavailableMeta}</SectionMeta>}
              </SectionBody>
              {!isDisabled && (
                <CloudDownload size="20" color="var(--color-text-secondary)" />
              )}
            </ClickableSection>
          </>
        )}

        <Divider />
        <ClickableSection
          type="button"
          $disabled={isDisabled}
          onClick={handlePracticeClick}
        >
          <SectionIcon $color={subjectColor} $disabled={isDisabled}>
            <Image
              src="batch-practice.svg"
              width={40}
              height={40}
              alt="Practice"
            />
          </SectionIcon>
          <SectionBody>
            <SectionTitle>{getPracticeTitle()}</SectionTitle>
            {getPracticeMeta() && (
              <SectionMeta>{getPracticeMeta()}</SectionMeta>
            )}
          </SectionBody>
        </ClickableSection>
      </Container>
    </Dialog>
  );
};

DppMoreDialog.propTypes = {
  showDialog: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  lesson: PropTypes.objectOf(PropTypes.any).isRequired,
  goalUID: PropTypes.string,
  source: PropTypes.string,
  isDisabled: PropTypes.bool,
  isLockedForPurchase: PropTypes.bool
};

DppMoreDialog.defaultProps = {
  goalUID: '',
  source: 'Course',
  isDisabled: false,
  isLockedForPurchase: false
};

export default DppMoreDialog;
